import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import api, { BASE_URL } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import "./UpgradePlans.css";

const formatMoney = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const formatCyclePrice = (plan) => `${formatMoney(plan.estimated_amount ?? plan.base_fee ?? plan.plan_price)} / month`;

function UpgradePlans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [expandedPlans, setExpandedPlans] = useState({});
  const [activePlan, setActivePlan] = useState(null);
  const [currentEmployeeCount, setCurrentEmployeeCount] = useState(0);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await api.get("/upgrade/plans", { timeout: 15000 });
      const payload = response.data?.data || {};
      setPlans(payload.plans || []);
      setActivePlanId(payload.active_plan_id || null);
      setActivePlan(payload.active_plan || null);
      setCurrentEmployeeCount(payload.current_employee_count || 0);

    } catch (error) {
      const message =
        error.code === "ECONNABORTED"
          ? "Upgrade plans are taking too long to load. Please try again."
          : error.response?.data?.message || "Please refresh and try again.";
      setLoadError(message);
      Swal.fire({
        icon: "error",
        title: "Unable to load plans",
        text: message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !user.is_owner) {
      navigate("/dashboard", { replace: true });
      return;
    }

    fetchPlans();
  }, [user, navigate, fetchPlans]);


  const toggleFeatures = (planId) => {
    setExpandedPlans((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }));
  };

  return (
    <MainLayout>
      <div className="upgrade-page">
        <div className="upgrade-shell">
          <div className="upgrade-heading-row">
            <div>
              <span className="upgrade-kicker">Pricing & Subscription</span>
              <h2>Upgrade</h2>
              <p>Review pricing, included employees, add-on rates, and your current usage before changing subscription.</p>
            </div>

            <div className="upgrade-heading-actions">
              <button
                type="button"
                className="upgrade-history-link"
                onClick={() => navigate("/plan-history")}
              >
                Payment History
              </button>
              <span className="upgrade-cycle-btn active">Monthly billing</span>
            </div>
          </div>

          {activePlan ? (
            <div className="upgrade-summary-banner">
              <div>
                <span className="upgrade-summary-label">Current plan</span>
                <h3>{activePlan.plan_name}</h3>
                <p>
                  {currentEmployeeCount} active employees, {activePlan.included_employees} included, {activePlan.additional_employees || 0} additional.
                </p>
              </div>
              <div className="upgrade-summary-metrics">
                <div>
                  <span>Current estimate</span>
                  <strong>{formatMoney(activePlan.estimated_amount)}</strong>
                </div>
                <div>
                  <span>Peak billing logic</span>
                  <strong>Highest active count</strong>
                </div>
                <div>
                  <span>Employee onboarding</span>
                  <strong>Unlimited</strong>
                </div>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="upgrade-loading-card">
              <div className="spinner"></div>
              <p>Loading plans...</p>
            </div>
          ) : loadError ? (
            <div className="upgrade-loading-card">
              <i className="fa-solid fa-triangle-exclamation"></i>
              <p>{loadError}</p>
              <button type="button" className="upgrade-history-link" onClick={fetchPlans}>
                Retry
              </button>
            </div>
          ) : (
            <div className="upgrade-grid">
              {plans.map((plan) => {
                const isActive = Number(activePlanId) === Number(plan.id);
                const visibleFeatures = expandedPlans[plan.id]
                  ? plan.visible_features || []
                  : (plan.visible_features || []).slice(0, 5);

                return (
                  <article
                    key={plan.id}
                    className={`upgrade-card ${plan.recommended ? "recommended" : ""} ${isActive ? "active-card" : ""}`}
                  >
                    {plan.recommended && (
                      <div className="upgrade-recommended">Recommended By Cedur</div>
                    )}
                    {isActive && (
                      <div className="upgrade-active-ribbon">Current Plan</div>
                    )}

                    <div className="upgrade-card-body">
                      <h3>{plan.plan_name}</h3>
                      <div className="upgrade-price">{formatCyclePrice(plan)}</div>
                      <p className="upgrade-plan-note">Base fee {formatMoney(plan.base_fee)} with {formatMoney(plan.add_on_rate)} per employee beyond {plan.included_employees} included seats.</p>

                      <div className="upgrade-card-metrics">
                        <div>
                          <span>Included</span>
                          <strong>{plan.included_employees || 0}</strong>
                        </div>
                        <div>
                          <span>Onboarding</span>
                          <strong>Unlimited</strong>
                        </div>
                        <div>
                          <span>Estimate</span>
                          <strong>{formatMoney(plan.estimated_amount)}</strong>
                        </div>
                      </div>

                      <div className="upgrade-usage-footnote">
                        Billing uses the highest active employee count during the cycle.
                      </div>

                      {isActive ? (
                        <button type="button" className="upgrade-action active" disabled>
                          Active Plan
                        </button>
                      ) : plan.plan_type === "custom" ? (
                        <a
                          href={`${BASE_URL}/contact-us`}
                          target="_blank"
                          rel="noreferrer"
                          className="upgrade-action contact"
                        >
                          Contact Sales
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="upgrade-action"
                          onClick={() => navigate(`/upgrade-plan/${plan.id}`)}
                        >
                          Upgrade Now
                        </button>
                      )}

                      <div className="upgrade-feature-list">
                        <div className="upgrade-feature-row">
                          <span>Add-on rate</span>
                          <strong>{formatMoney(plan.add_on_rate)}</strong>
                        </div>

                        {visibleFeatures.map((feature) => (
                          <div className="upgrade-feature-row" key={`${plan.id}-${feature}`}>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      {(plan.visible_features || []).length > 5 && (
                        <button
                          type="button"
                          className="upgrade-view-toggle"
                          onClick={() => toggleFeatures(plan.id)}
                        >
                          {expandedPlans[plan.id] ? "View Less" : "View All"}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default UpgradePlans;
